import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLiveOverview } from '@/contexts/LiveOverviewContext';
import { isChannelBlocked } from '@/lib/channelHealth';
import { displayName, formatDayLabel, formatPhone, formatTime } from '@/lib/conversationFormat';
import { canReplyToThread, isAgentPaused } from '@/lib/liveConversation';
import { cn } from '@/lib/utils';
import ChannelBlockedBanner from './ChannelBlockedBanner';
import ChatHeader, { TestMark } from './ChatHeader';
import { ContactContext, type AgentLabel } from './ContextPanel';
import HandoffStatusBar from './HandoffStatusBar';
import LiveChatComposer from './LiveChatComposer';
import MessageTimeline, { type SessionJump } from './MessageTimeline';
import { useContactActions } from './useContactActions';
import { useContactThread } from './useContactThread';
import { useServerNow } from './useServerNow';
import { ChatError, ChatSkeleton, WorkspaceFrame, type WorkspaceChrome } from './WorkspaceFrame';

type Props = WorkspaceChrome & {
  contactKey: string;
  me: string | null;
  agentLabel?: AgentLabel;
  /** Put the cursor into the reply box once the chat is there (`focus=composer`). */
  focusComposer?: boolean;
  onComposerFocused?: () => void;
  /** Scroll to this session once loaded (an old `?c=<id>` link resolved to this contact). */
  initialSession?: number | null;
  /** The initial session jump was done (or given up): the page forgets it. */
  onInitialSessionHandled?: () => void;
};

function focusTextarea(container: HTMLElement | null): boolean {
  const textarea = container?.querySelector<HTMLTextAreaElement>('textarea');
  if (!textarea || textarea.disabled) return false;
  textarea.focus();
  return true;
}

/**
 * One WhatsApp contact as a continuous chat (livecenter §5): header, the
 * blocked-number banner, handoff status bar, all sessions with separators,
 * the reply box, and the context.
 */
export function ContactWorkspace({
  contactKey,
  me,
  agentLabel,
  focusComposer = false,
  onComposerFocused,
  initialSession = null,
  onInitialSessionHandled,
  layout,
  contextOpen,
  onContextOpenChange,
  onBack,
}: Props) {
  const headingId = useId();
  const state = useContactThread(contactKey, me);
  const { thread } = state;
  // A refused send (blocked number) fetches the thread again for the reason.
  const actions = useContactActions(contactKey, state.update, me, { onChannelBlocked: state.refresh });
  const { refresh: refreshOverview } = useLiveOverview();
  const now = useServerNow(30_000);
  const composerRef = useRef<HTMLDivElement>(null);
  const [jump, setJump] = useState<SessionJump | null>(null);
  const jumpedToInitialRef = useRef(false);

  const { ensureSession } = state;
  const jumpToSession = useCallback(
    async (conversationId: number) => {
      if (layout !== 'wide') onContextOpenChange(false);
      if (await ensureSession(conversationId)) {
        setJump({ conversationId, nonce: Date.now() });
      }
    },
    [layout, onContextOpenChange, ensureSession],
  );

  useEffect(() => {
    if (!thread || initialSession == null || jumpedToInitialRef.current) return;
    jumpedToInitialRef.current = true;
    void jumpToSession(initialSession).finally(() => onInitialSessionHandled?.());
  }, [thread, initialSession, jumpToSession, onInitialSessionHandled]);

  const loaded = !!thread;
  useEffect(() => {
    if (!focusComposer || !loaded) return;
    // After the composer rendered (layout + list heights settled).
    const id = requestAnimationFrame(() => {
      focusTextarea(composerRef.current);
      onComposerFocused?.();
    });
    return () => cancelAnimationFrame(id);
  }, [focusComposer, loaded, onComposerFocused]);

  const withOverviewRefresh = (action: () => Promise<void>) => async () => {
    await action();
    refreshOverview();
  };

  if (state.loading && !thread) return <ChatSkeleton onBack={onBack} />;
  if (!thread) {
    return <ChatError message={state.error ?? 'Chat nicht gefunden.'} onBack={onBack} />;
  }

  const canReply = canReplyToThread(thread, now);
  const agentLine = thread.agent_number ? agentLabel?.(thread.agent_number) ?? formatPhone(thread.agent_number) : null;
  const until = thread.reply_window_until;
  const windowText = canReply
    ? until
      ? `Fenster offen bis ${formatDayLabel(until, now)?.toLowerCase()} ${formatTime(until)}`
      : 'Fenster offen'
    : 'Antwortfenster geschlossen';
  const hasTestSession = thread.sessions.length > 0 && thread.sessions.every((s) => s.is_test);
  const channelBlocked = isChannelBlocked(thread.channel);

  const chat = (
    <>
      <ChatHeader
        headingId={headingId}
        name={displayName(thread.customer_name, thread.customer_phone)}
        avatarName={thread.customer_name}
        avatarPhone={thread.customer_phone}
        meta={[
          thread.customer_name?.trim() ? formatPhone(thread.customer_phone) : null,
          agentLine ? `über ${agentLine}` : null,
          windowText,
        ]
          .filter(Boolean)
          .join(' · ')}
        badges={hasTestSession ? <TestMark /> : null}
        onBack={onBack}
        onToggleContext={() => onContextOpenChange(!contextOpen)}
        contextOpen={contextOpen}
      />

      {channelBlocked && thread.channel && <ChannelBlockedBanner channel={thread.channel} now={now} />}

      {thread.live_chat_enabled && (
        <HandoffStatusBar
          takeover={thread.takeover}
          replyWindowUntil={thread.reply_window_until}
          canReply={canReply}
          channelBlocked={channelBlocked}
          me={me}
          customerPhone={thread.customer_phone}
          busy={actions.busy}
          onTakeover={withOverviewRefresh(actions.takeover)}
          onRelease={withOverviewRefresh(actions.release)}
          onExtend={actions.extend}
          onResolve={withOverviewRefresh(actions.resolveCallback)}
          onReplyNow={() => focusTextarea(composerRef.current)}
        />
      )}

      <MessageTimeline
        messages={thread.messages}
        sessions={thread.sessions}
        channel="whatsapp"
        me={me}
        hasMore={thread.has_more}
        loadingOlder={state.loadingOlder}
        onLoadOlder={state.loadOlder}
        agentTyping={state.agentTyping}
        staffTyping={state.staffTyping}
        jump={jump}
      />

      {thread.live_chat_enabled && (
        <div
          ref={composerRef}
          className={cn(
            'flex-shrink-0 border-t border-border bg-card px-3 pb-3 sm:px-5',
            layout === 'mobile' && 'pb-[max(0.75rem,env(safe-area-inset-bottom))]',
          )}
        >
          <LiveChatComposer
            canReply={canReply}
            channelBlocked={channelBlocked}
            takeoverActive={isAgentPaused(thread.takeover)}
            sending={actions.sending}
            onSend={async (text) => {
              const ok = await actions.send(text);
              if (ok) refreshOverview();
              return ok;
            }}
            contactKey={contactKey}
            onTypingHeartbeat={actions.typingHeartbeat}
          />
        </div>
      )}
    </>
  );

  return (
    <WorkspaceFrame
      layout={layout}
      contextOpen={contextOpen}
      onContextOpenChange={onContextOpenChange}
      onBack={onBack}
      labelledBy={headingId}
      chat={chat}
      context={<ContactContext thread={thread} me={me} agentLabel={agentLabel} onJumpToSession={jumpToSession} />}
    />
  );
}

export default ContactWorkspace;
